import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {Container, Grid, Stack, Title, Text, Group, Button, Badge, Paper, Menu} from '@mantine/core';
import {
    IconStar,
    IconStarFilled,
    IconPencil,
    IconFolder,
    IconFile,
    IconRefresh,
    IconUpload,
    IconDownload,
    IconHistory,
    IconEye,
    IconEyeOff,
    IconExternalLink,
    IconSettings,
    IconFileUpload,
} from '@tabler/icons-react';
import {useAction, useBatchedSelectors, useSelectorWithRefresh} from '@/store/kysely_module';
import {FileTableWithActions} from './components/FileTableWithActions';
import {useModule} from '@/hooks/useModule';
import {useModals} from '@/modules/music_sniper/components/modals/modals_provider';
import {Actions} from '@/store/actions/actions';
import {AttributesBlocksHeader, AttributesBlocksBody, useAttributesBlocksState, AttributesBlocksProps} from '@/components/attribute_editors';
import {ProjectTable} from '@/modules/music_sniper/pages/project_selection_page';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useOptimistic} from '@/hooks/useOptimistic';
import {useQuery, useMutation} from '@tanstack/react-query';
import {notifications} from '@mantine/notifications';
import {useCurrentUser} from '@/modules/music_sniper/hooks/useCurrentUser';
import {AttributeName} from '@/store/kysely_types';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {DashboardCard} from '../new_dashboard/components/DashboardCard';
import {OpenUploadFileModalButton} from '@/modules/music_sniper/components/modals/upload_file_modal';
import {useIsMobile} from '@/utils/user_agent';
import {WaveformCard} from '@/components/WaveformCard';
import {LoadingIndicator} from '../common/LoadingIndicator';

function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type Props = {
    projectId: string;
    workspaceId: string;
}

export const NewProjectViewPage = (props: Props) => {
    const [showHiddenSubProjects, setShowHiddenSubProjects] = useState(false);

    const {results, isLoading} = useBatchedSelectors({
        project: {queryName: 'getProject', args: {project_id: props.projectId, includeHidden: false}, defaultValue: null, workspaceContext: {workspace_id: props.workspaceId}},
        allSubProjects: {queryName: 'getProject', args: {project_id: props.projectId, includeHidden: true}, defaultValue: null, workspaceContext: {workspace_id: props.workspaceId}},
        workspace: {queryName: 'getWorkspace', args: {workspace_id: props.workspaceId}, defaultValue: null, workspaceContext: {workspace_id: props.workspaceId}},
    });

    const {results: filesResults, isLoading: isLoadingFiles} = useBatchedSelectors({
        files: {queryName: 'getFilesForProject', args: {project_id: props.projectId}, defaultValue: [], workspaceContext: {workspace_id: props.workspaceId}},
    });

    const [project, setProject] = useOptimistic(results.project.data);

    const launcherFile = project?.attributes.launcher_file
        ? filesResults.files.data.find(f => f.relative_path === project.attributes.launcher_file)
        : undefined;

    const launcherPathExists = project?.attributes.launcher_file
        ? filesResults.files.data.some(f =>
            f.relative_path === project.attributes.launcher_file ||
            f.relative_path.startsWith(project.attributes.launcher_file + '/')
        )
        : false;

    const bounceFile = project?.attributes.bounce_file
        ? filesResults.files.data.find(f => f.relative_path === project.attributes.bounce_file)
        : undefined;

    const [bounceWaveform, , isLoadingBounceWaveform] = useSelectorWithRefresh(
        'getFileWaveformAndDuration',
        {file_id: bounceFile?.id || ''},
        null,
        {workspace_id: props.workspaceId, ignore: !bounceFile?.id}
    );

    const navigate = useNavigate();
    const localDesktopFilesModule = useModule('LocalDesktopFiles');

    const attributesBlocksProps: AttributesBlocksProps = {
        values: project?.attributes,
        parent_id: props.projectId,
        parent_type: 'Project',
        workspace_id: props.workspaceId,
        refresh: results.project.refetch,
    };
    const attributesBlocksHookState = useAttributesBlocksState(attributesBlocksProps);

    const workspaceId = props.workspaceId;

    const modals = useModals();

    const actions = {
        updateProjectName: useAction('updateProjectName'),
        addFileToProject: useAction('addFileToProject'),
        createProject: useAction('createProject'),
        updateObjectAttribute: useAction('updateObjectAttribute'),
        deleteFile: useAction('deleteFile'),
        starObject: useAction('starObject'),
        unstarObject: useAction('unstarObject'),
    } as Pick<Actions, 'updateProjectName' | 'addFileToProject' | 'createProject' | 'updateObjectAttribute' | 'deleteFile' | 'starObject' | 'unstarObject'>;

    const makeLauncherFile = async (relativePath: string) => {
        await actions.updateObjectAttribute({
            parent_id: props.projectId,
            parent_type: 'Project',
            attribute: AttributeName.launcher_file,
            value: relativePath,
        }, {workspace_id: props.workspaceId});

        results.project.refetch();
    };

    const makeBounceFile = async (relativePath: string) => {
        await actions.updateObjectAttribute({
            parent_id: props.projectId,
            parent_type: 'Project',
            attribute: AttributeName.bounce_file,
            value: relativePath,
        }, {workspace_id: props.workspaceId});

        results.project.refetch();
    };

    const kyselyModule = useModule('Kysely');
    const currentUser = useCurrentUser();
    const msFilesModule = useModule('MSFiles');
    const remoteFilesModule = useModule('RemoteFileClient');

    const isWorkspaceOwner = currentUser?.id === results.workspace.data?.owner_id;
    const hasHiddenSubProjects = (results.allSubProjects.data?.sub_projects?.length || 0) > (results.project.data?.sub_projects?.length || 0);
    const hiddenSubProjects = results.allSubProjects.data?.sub_projects?.filter(p => p.hidden_at !== null) || [];

    const isMobile = useIsMobile();

    const {data: dirtyFilesData, isLoading: isCheckingFiles, refetch: refreshDirtyFiles} = useQuery({
        queryKey: ['dirtyFiles', props.projectId],
        queryFn: async () => {
            if (!localDesktopFilesModule?.TauriProjectSync) {
                return null;
            }
            const sync = new localDesktopFilesModule.TauriProjectSync(
                kyselyModule,
                localDesktopFilesModule,
                currentUser!,
                msFilesModule,
                remoteFilesModule,
            );
            return sync.getDirtyFilesForProject(props.projectId);
        },
        enabled: Boolean(localDesktopFilesModule?.TauriProjectSync),
    });

    const openEditProjectNameModal = () => {
        modals.openModal('TextInputModal', {
            title: 'Rename project',
            inputName: 'Project name',
            initialValue: project?.name,
            onSubmit: async (projectName) => {
                setProject(prev => {
                    if (!prev) {
                        return prev;
                    }
                    return {
                        ...prev,
                        name: projectName,
                    };
                });
                await actions.updateProjectName({project_id: props.projectId, new_name: projectName}, {workspace_id: props.workspaceId});
                results.project.refetch();
            },
        });
    };

    const uploadFilesMutation = useMutation({
        mutationFn: async () => {
            if (!localDesktopFilesModule?.TauriProjectSync) {
                throw new Error('TauriProjectSync not available');
            }

            const sync = new localDesktopFilesModule.TauriProjectSync(
                kyselyModule,
                localDesktopFilesModule,
                currentUser!,
                msFilesModule,
                remoteFilesModule,
            );

            const result = await sync.uploadFilesForProject(props.projectId);

            if (result.success && result.uploadedCount === 0) {
                notifications.show({
                    title: '✅ All files already up to date',
                    message: '',
                    color: 'green',
                });
            }

            return result;
        },
        onSuccess: async () => {
            await refreshDirtyFiles();
            filesResults.files.refetch();
            results.project.refetch();
        },
    });

    const downloadFilesMutation = useMutation({
        mutationFn: async () => {
            if (!localDesktopFilesModule?.TauriProjectSync) {
                throw new Error('TauriProjectSync not available');
            }

            const sync = new localDesktopFilesModule.TauriProjectSync(
                kyselyModule,
                localDesktopFilesModule,
                currentUser!,
                msFilesModule,
                remoteFilesModule,
            );

            const firstFile = filesResults.files.data[0];
            if (firstFile) {
                const result = await sync.syncLocalProjectWithRemoteProject({
                    project_id: props.projectId,
                    relative_path: firstFile.relative_path || firstFile.name,
                    workspace_id: props.workspaceId
                });

                if (result && 'full_path' in result && result.full_path) {
                    notifications.show({
                        title: '✅ Project downloaded successfully',
                        message: `Downloaded to: ${result.full_path.split('/').slice(-2).join('/')}`,
                        color: 'green',
                    });
                }
            }
        },
        onSuccess: async () => {
            await refreshDirtyFiles();
        },
    });

    const handleUploadFiles = () => {
        uploadFilesMutation.mutate();
    };

    const handleDownloadFiles = () => {
        downloadFilesMutation.mutate();
    };

    if ((isLoading && !results?.project?.data) || (isLoadingFiles && filesResults?.files?.data.length === 0)) {
        return <LoadingIndicator />;
    }

    const dirtyFiles = dirtyFilesData?.dirtyFiles || [];
    const hasLocalProject = dirtyFilesData?.hasLocalProject || false;
    const filesToDownload = dirtyFiles.filter(f => f.status === 'missing-locally' || f.status === 'remote-newer');
    const filesToUpload = dirtyFiles.filter(f => f.status === 'dirty' || f.status === 'local-only');

    const addAttributesHeader = (
        <AttributesBlocksHeader
            {...attributesBlocksProps}
            hookState={attributesBlocksHookState}
            plusMenuItems={[
                <Menu.Item
                    key='add-sub-project'
                    leftSection={<IconFolder size={14} />}
                    onClick={() => {
                        modals.openModal('TextInputModal', {
                            title: 'Add Sub-project',
                            inputName: 'Sub-project name',
                            onSubmit: async (subProjectName) => {
                                const subProject = await actions.createProject({
                                    name: subProjectName,
                                    parent_id: props.projectId,
                                    workspace_id: props.workspaceId,
                                }, {workspace_id: props.workspaceId});
                                if (!subProject) {
                                    console.error('Failed to create sub-project');
                                    return;
                                }

                                navigateToPathForReason(navigate, 'navigate_to_project_page_from_project_view_page', `/workspaces/${props.workspaceId}/projects/${subProject.id}`);
                            },
                        });
                    }}
                >
                    Add Sub-project
                </Menu.Item>,
            ]}
            dotMenuItems={[
                <Menu.Item
                    key='edit-project-name'
                    leftSection={<IconPencil size={14} />}
                    onClick={openEditProjectNameModal}
                >
                    Rename Project
                </Menu.Item>,
                project?.isStarred ? (
                    <Menu.Item
                        key='unstar-project'
                        leftSection={<IconStarFilled size={14} color='gold' />}
                        onClick={async () => {
                            modals.openModal('ConfirmationModal', {
                                title: 'Remove star from project?',
                                message: 'The project will no longer appear in your starred projects.',
                                confirmLabel: 'Remove',
                                onConfirm: async () => {
                                    await actions.unstarObject({parent_id: props.projectId, parent_type: 'Project'}, {workspace_id: props.workspaceId});
                                    results.project.refetch();
                                },
                            });
                        }}
                    >
                        Unstar Project
                    </Menu.Item>
                ) : (
                    <Menu.Item
                        key='star-project'
                        leftSection={<IconStar size={14} />}
                        onClick={async () => {
                            await actions.starObject({parent_id: props.projectId, parent_type: 'Project'}, {workspace_id: props.workspaceId});
                            results.project.refetch();
                        }}
                    >
                        Star Project
                    </Menu.Item>
                ),
            ]}
        />
    );

    return (
        <Container size="xl" py={{base: 'xs', sm: 'md'}} px={{base: 'xs', sm: 'md'}}>
            <Stack gap="md">
                {!isMobile && (
                    <Group justify="space-between" wrap="nowrap" align="center">
                        <Title order={2}>
                            {project?.name}
                        </Title>
                    </Group>
                )}

                <Grid gutter="md">
                    {!isLoadingFiles && bounceFile && (
                        <Grid.Col span={12}>
                            <WaveformCard
                                fileInfo={bounceFile}
                                waveformData={bounceWaveform}
                                isLoadingWaveform={isLoadingBounceWaveform}
                                title={(
                                    <span style={{cursor: 'pointer'}} onClick={() => {
                                        navigateToPathForReason(
                                            navigate,
                                            'navigate_to_file_page_from_project_view_page',
                                            `/workspaces/${props.workspaceId}/files/${bounceFile.id}`
                                        )
                                    }}>
                                        {bounceFile.name}
                                    </span>
                                )}
                                centered={true}
                            />
                        </Grid.Col>
                    )}

                    {!isLoadingFiles && project?.attributes && (
                        <Grid.Col span={12}>
                            <DashboardCard
                                title={(
                                    <>
                                        Project Details
                                        <Group
                                            display='inline-block'
                                            ml="md"
                                        >
                                            {addAttributesHeader}
                                        </Group>
                                    </>
                                )}
                                icon={<IconSettings size={18} />}
                            >
                                <Group gap="md" mb="md">
                                    {Boolean(project?.num_revisions) && project?.num_revisions && (
                                        <Group gap="xs">
                                            <IconHistory size={18} />
                                            <Text size="sm">{project.num_revisions} revision{project.num_revisions !== 1 ? 's' : ''}</Text>
                                        </Group>
                                    )}
                                    {bounceFile?.duration && (
                                        <Group gap="xs">
                                            <IconFile size={18} />
                                            <Text size="sm">Duration: {formatDuration(Number(bounceFile.duration))}</Text>
                                        </Group>
                                    )}
                                </Group>

                                <AttributesBlocksBody
                                    {...attributesBlocksProps}
                                    hookState={attributesBlocksHookState}
                                />
                            </DashboardCard>
                        </Grid.Col>
                    )}

                    {!isLoadingFiles && (Boolean(project?.sub_projects.length)) && (
                        <Grid.Col span={12}>
                            <DashboardCard
                                title="Sub-projects"
                                icon={<IconFolder size={18} />}
                                headerActions={
                                    isWorkspaceOwner && hasHiddenSubProjects ? (
                                        <Button
                                            variant="subtle"
                                            size="xs"
                                            leftSection={showHiddenSubProjects ? <IconEye size={16} /> : <IconEyeOff size={16} />}
                                            onClick={() => setShowHiddenSubProjects(!showHiddenSubProjects)}
                                        >
                                            {showHiddenSubProjects ? 'Hide hidden' : 'Show hidden'}
                                        </Button>
                                    ) : undefined
                                }
                            >
                                {project?.sub_projects && project.sub_projects.length > 0 ? (
                                    <>
                                        <ProjectTable
                                            onClick={projectId => navigateToPathForReason(navigate, 'navigate_to_project_page_from_project_view_page', `/workspaces/${props.workspaceId}/projects/${projectId}`)}
                                            projects={project.sub_projects}
                                            refresh={() => {
                                                results.project.refetch();
                                                results.allSubProjects.refetch();
                                            }}
                                            isWorkspaceOwner={isWorkspaceOwner}
                                            showHidden={false}
                                            parentProjectId={props.projectId}
                                            hasHiddenProjects={hasHiddenSubProjects}
                                        />

                                        {showHiddenSubProjects && hiddenSubProjects.length > 0 && (
                                            <div style={{marginTop: '1rem'}}>
                                                <Text size="sm" fw={600} mb="xs">Hidden Sub-projects</Text>
                                                <ProjectTable
                                                    onClick={projectId => navigateToPathForReason(navigate, 'navigate_to_project_page_from_project_view_page', `/workspaces/${props.workspaceId}/projects/${projectId}`)}
                                                    projects={hiddenSubProjects}
                                                    refresh={() => {
                                                        results.project.refetch();
                                                        results.allSubProjects.refetch();
                                                    }}
                                                    isWorkspaceOwner={isWorkspaceOwner}
                                                    showHidden={true}
                                                    parentProjectId={props.projectId}
                                                    hasHiddenProjects={hasHiddenSubProjects}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Text size="sm" c="dimmed">No sub-projects</Text>
                                )}
                            </DashboardCard>
                        </Grid.Col>
                    )}

                    {!isLoadingFiles && localDesktopFilesModule?.TauriProjectSync && (
                        <Grid.Col span={12}>
                            <DashboardCard
                                title="File Sync"
                                icon={<IconRefresh size={18} />}
                                headerActions={
                                    <Group gap="xs">
                                        <Button
                                            size="xs"
                                            variant="subtle"
                                            leftSection={<IconRefresh size={16} />}
                                            onClick={() => refreshDirtyFiles()}
                                            loading={isCheckingFiles}
                                        >
                                            Refresh
                                        </Button>
                                        {hasLocalProject && launcherPathExists && localDesktopFilesModule?.openWithFileHelpers && project && (
                                            <localDesktopFilesModule.openWithFileHelpers.OpenWithComponent
                                                projectId={props.projectId}
                                                relativePath={project.attributes.launcher_file!}
                                                workspaceId={props.workspaceId}
                                                includeLabel={true}
                                            />
                                        )}
                                    </Group>
                                }
                            >

                                {dirtyFilesData && !isCheckingFiles && (
                                    <Paper p="sm" withBorder={dirtyFiles.length > 0 || !hasLocalProject}>
                                        {dirtyFilesData.error && (
                                            <Text size="sm" c="dimmed">
                                                Error: {dirtyFilesData.error}
                                            </Text>
                                        )}
                                        {!hasLocalProject ? (
                                            filesResults.files.data.length === 0 ? (
                                                <Text size="sm" c="dimmed">
                                                    📁 Project has no files yet
                                                </Text>
                                            ) : (
                                                <Text size="sm" c="dimmed">
                                                    📥 Project not downloaded locally yet
                                                </Text>
                                            )
                                        ) : dirtyFiles.length > 0 ? (
                                            <>
                                                <Text size="sm" fw={500} mb="xs">
                                                    Files with local changes:
                                                </Text>
                                                <ul style={{margin: 0, paddingLeft: '20px'}}>
                                                    {dirtyFiles.map((file, index) => (
                                                        <li key={index}>
                                                            <Text size="sm" component="div">
                                                                {file.relativePath}
                                                                {' '}
                                                                <Badge size="xs" variant="light" color={
                                                                    file.status === 'dirty' ? 'orange' :
                                                                        file.status === 'local-only' ? 'blue' :
                                                                            file.status === 'missing-locally' ? 'green' :
                                                                                'red'
                                                                }>
                                                                    {file.status === 'dirty' && 'Modified - Ready to Upload'}
                                                                    {file.status === 'local-only' && 'New Local - Ready to Upload'}
                                                                    {file.status === 'missing-locally' && 'New Remote - Ready to Download'}
                                                                    {file.status === 'remote-deleted' && 'Remote Deleted'}
                                                                    {file.status === 'remote-newer' && 'Remote Updated - Ready to Download'}
                                                                </Badge>
                                                            </Text>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            !dirtyFilesData.error && (
                                                <Text size="sm">
                                                    ✅ All files are in sync
                                                </Text>
                                            )
                                        )}
                                    </Paper>
                                )}

                                <Group mt="sm">
                                    {hasLocalProject ? (
                                        <>
                                            {filesToUpload.length > 0 && (
                                                <Button
                                                    leftSection={<IconUpload size={16} />}
                                                    onClick={handleUploadFiles}
                                                    disabled={isCheckingFiles || uploadFilesMutation.isPending || downloadFilesMutation.isPending}
                                                    loading={uploadFilesMutation.isPending}
                                                >
                                                    Upload Project Files ({filesToUpload.length})
                                                </Button>
                                            )}
                                            {filesToDownload.length > 0 && (
                                                <Button
                                                    leftSection={<IconDownload size={16} />}
                                                    onClick={handleDownloadFiles}
                                                    disabled={isCheckingFiles || uploadFilesMutation.isPending || downloadFilesMutation.isPending}
                                                    loading={downloadFilesMutation.isPending}
                                                >
                                                    Download Updated Files ({filesToDownload.length})
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <Button
                                            leftSection={<IconDownload size={16} />}
                                            onClick={handleDownloadFiles}
                                            disabled={isCheckingFiles || uploadFilesMutation.isPending || downloadFilesMutation.isPending || filesResults.files.data.length === 0}
                                            loading={downloadFilesMutation.isPending}
                                        >
                                            Download Project to Local
                                        </Button>
                                    )}
                                </Group>
                            </DashboardCard>
                        </Grid.Col>
                    )}

                    {!isLoadingFiles && (
                        <Grid.Col span={12}>
                            <DashboardCard
                                title="Files"
                                icon={<IconFile size={18} />}
                                headerActions={
                                    <OpenUploadFileModalButton component={({onClick}) => (
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconFileUpload size={16} />}
                                            onClick={onClick}
                                        >
                                            Upload New File
                                        </Button>
                                    )} />
                                }
                            >
                                {filesResults.files.data.length > 0 ? (
                                    <FileTableWithActions
                                        fileInfos={filesResults.files.data}
                                        makeLauncherFile={makeLauncherFile}
                                        makeBounceFile={makeBounceFile}
                                        onItemClick={fileInfo => {
                                            navigateToPathForReason(navigate, 'navigate_to_file_page_from_project_view_page', `/workspaces/${workspaceId}/files/${fileInfo.id}`);
                                        }}
                                        refresh={filesResults.files.refetch}
                                        projectId={props.projectId}
                                        workspaceId={props.workspaceId}
                                    />
                                ) : (
                                    <Text size="sm" c="dimmed">No files</Text>
                                )}
                            </DashboardCard>
                        </Grid.Col>
                    )}
                </Grid>
            </Stack>
        </Container>
    );
};
